import { useCallback, useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import PrivacyPolicyDialog from "@/components/footer/privacy_policy_dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useLanguage } from "@/context/LanguageContext";
import { cookieConsentLabels } from "@/i18n";
import { cn } from "@/lib/utils";
import {
  COOKIE_CONSENT_OPEN_EVENT,
  type CookieConsentChoice,
  hasValidCookieConsent,
  readCookieConsent,
  saveCookieConsent,
} from "@/lib/cookieConsent";
import {
  initGtagConsentDefaults,
  syncGtagWithMarketingConsent,
} from "@/lib/gtag";

/**
 * First-visit cookie choice (all vs necessary) + reopen for change/withdraw.
 * Desktop: dialog. Mobile web + native: bottom sheet.
 * Persists in localStorage (~1 year). Gates Google Ads gtag.
 */
export function CookieConsentHost() {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const useSheet = isMobile || isNative;
  const { language } = useLanguage();
  const t = cookieConsentLabels(language);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<CookieConsentChoice>("all");
  const [openReason, setOpenReason] = useState<
    "settings" | "valuation_requires_marketing"
  >("settings");
  const [policyOpen, setPolicyOpen] = useState(false);

  const syncFromStorage = useCallback(() => {
    const existing = readCookieConsent();
    if (existing) {
      setSelected(existing.choice);
      syncGtagWithMarketingConsent(existing.choice === "all");
      return true;
    }
    syncGtagWithMarketingConsent(false);
    return false;
  }, []);

  useEffect(() => {
    // Consent Mode defaults before any Ads tag can load
    initGtagConsentDefaults();
    const has = syncFromStorage();
    if (!has) {
      // Slight delay so layout settles; stay above app-download (gated until consent)
      const id = window.setTimeout(() => {
        setEditing(false);
        setOpenReason("settings");
        setSelected("all");
        setOpen(true);
      }, 400);
      return () => window.clearTimeout(id);
    }
  }, [syncFromStorage]);

  useEffect(() => {
    const onOpen = (ev: Event) => {
      const detail = (
        ev as CustomEvent<{
          edit?: boolean;
          reason?: "settings" | "valuation_requires_marketing";
        }>
      ).detail;
      const existing = readCookieConsent();
      setEditing(detail?.edit !== false && !!existing);
      setOpenReason(detail?.reason ?? "settings");
      setSelected(existing?.choice ?? "all");
      setOpen(true);
    };
    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, onOpen);
  }, []);

  const applyChoice = (choice: CookieConsentChoice) => {
    saveCookieConsent(choice);
    syncGtagWithMarketingConsent(choice === "all");
    setSelected(choice);
    setOpen(false);
    setEditing(false);
    setOpenReason("settings");
  };

  const choiceHint =
    editing &&
    openReason !== "valuation_requires_marketing" &&
    (selected === "all" ? t.currentChoiceAll : t.currentChoiceNecessary);

  const body = (
    <div className="space-y-4">
      {openReason === "valuation_requires_marketing" ? (
        <p className="text-sm text-foreground leading-relaxed font-medium">
          {t.valuationRequiresMarketing}
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground leading-relaxed">{t.body}</p>
      {choiceHint ? (
        <p className="text-xs font-medium text-foreground/80">{choiceHint}</p>
      ) : null}
      <p className="text-xs text-muted-foreground">{t.changeHint}</p>

      <div className="flex flex-col gap-2.5">
        <Button
          type="button"
          className={cn(
            "w-full h-11 rounded-xl font-semibold",
            editing && selected === "all" && "ring-2 ring-primary ring-offset-2",
          )}
          onClick={() => applyChoice("all")}
        >
          {t.acceptAll}
        </Button>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full h-11 rounded-xl font-medium",
            editing &&
              selected === "necessary" &&
              "ring-2 ring-primary ring-offset-2",
          )}
          onClick={() => applyChoice("necessary")}
        >
          {t.necessaryOnly}
        </Button>
        <button
          type="button"
          className="text-sm text-primary hover:underline font-medium py-1"
          onClick={() => setPolicyOpen(true)}
        >
          {t.readPolicy}
        </button>
      </div>
    </div>
  );

  // Hide consent while reading the policy so it isn't covered by z-[10100].
  // Keep `open` true so consent returns when the policy closes.
  const consentUiOpen = open && !policyOpen;

  const onConsentOpenChange = (next: boolean) => {
    if (policyOpen) return;
    // Require an explicit choice on first visit
    if (!next && !hasValidCookieConsent()) return;
    setOpen(next);
  };

  return (
    <>
      {useSheet ? (
        <Sheet open={consentUiOpen} onOpenChange={onConsentOpenChange}>
          <SheetContent
            side="bottom"
            className={cn(
              "rounded-t-2xl px-5 pt-3 pb-6 gap-0 z-[10100]",
              isNative && "native-sheet-compact",
            )}
            overlayClassName="z-[10090] bg-black/50"
            onPointerDownOutside={(e) => {
              if (!hasValidCookieConsent()) e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              if (!hasValidCookieConsent()) e.preventDefault();
            }}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted-foreground/25" />
            <SheetHeader className="text-left space-y-2 pb-2">
              <div className="flex items-center gap-2.5">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary">
                  <Cookie className="h-4 w-4" strokeWidth={2} />
                </span>
                <SheetTitle className="text-lg">{t.title}</SheetTitle>
              </div>
              <SheetDescription className="sr-only">{t.body}</SheetDescription>
            </SheetHeader>
            {body}
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={consentUiOpen} onOpenChange={onConsentOpenChange}>
          <DialogContent
            className="sm:max-w-md rounded-2xl z-[10100]"
            showCloseButton={editing || hasValidCookieConsent()}
            onPointerDownOutside={(e) => {
              if (!hasValidCookieConsent()) e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              if (!hasValidCookieConsent()) e.preventDefault();
            }}
          >
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-2.5">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary">
                  <Cookie className="h-4 w-4" strokeWidth={2} />
                </span>
                <DialogTitle className="text-xl">{t.title}</DialogTitle>
              </div>
              <DialogDescription className="sr-only">{t.body}</DialogDescription>
            </DialogHeader>
            {body}
          </DialogContent>
        </Dialog>
      )}

      <PrivacyPolicyDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        initialTab="cookies"
      />
    </>
  );
}
